import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { loadUserProfile } from "./services/storageService";
import { useElementStore } from "./store/elementStore";
import { ThemeProvider } from "./contexts/ThemeContext";
import { initSounds, playSound, updateSoundPreferences } from "./services/soundService";
import { initAuth, syncGameState, getUserBestScore } from "./services/firebaseService";
import { initCapacitor } from "./services/capacitorService";
import { initRewardedAds } from "./services/rewardedAdService";
import { ELEMENT_DEFS } from "./components/elementDefinitions";

// Redesigned screens (self-contained — each renders its own Screen shell)
import HomeScreen from "./components/screens/HomeScreen";
import ForgeScreen from "./components/screens/ForgeScreen";
import Screen from "./components/ui/Screen";
import CodexScreen from "./components/screens/CodexScreen";
import ProfileScreen from "./components/screens/ProfileScreen";
import RanksScreen from "./components/screens/RanksScreen";
import SettingsScreen from "./components/screens/SettingsScreen";
import AuthScreen from "./components/screens/AuthScreen";

// Existing routes not yet redesigned (restyled in later stages)
import ForumList from "./components/forum/ForumList";
import TopicList from "./components/forum/TopicList";
import MessageThread from "./components/forum/MessageThread";
import BattleArena from "./components/game-modes/BattleArena";
import DeleteAccount from "./components/DeleteAccount";
import PrivacyPolicy from "./components/PrivacyPolicy";
import CompletionOverlay from "./components/screens/CompletionOverlay";

// Game modes enum (retained — Home routes to /play with a mode in location state)
export enum GameMode {
  FREE_FORGE = "Free Forge",
  TIME_ATTACK = "Time Attack",
  DAILY_PUZZLE = "Daily Puzzle",
  GAUNTLET = "Gauntlet",
  LAB_MODE = "Lab Mode",
  BATTLE_ARENA = "Battle Arena",
}

// Per-route document title + canonical (§12 A5). Googlebot renders JS, so this
// gives per-route SEO signals without a helmet dependency.
const ROUTE_TITLES: Record<string, string> = {
  "/": "MystiCrafter — Alchemy Puzzle Game | Combine 140+ Elements",
  "/play": "Play MystiCrafter — Free Forge & Game Modes",
  "/codex": "MystiCrafter Codex — Every Element & Recipe",
  "/ranks": "MystiCrafter Leaderboards — Top Alchemists",
  "/battle": "MystiCrafter Battle Arena — Live 1v1 Alchemy Races",
  "/you": "Your MystiCrafter Profile & Achievements",
  "/settings": "MystiCrafter Settings & Account",
  "/auth": "Sign in to MystiCrafter",
  "/forums": "MystiCrafter Community Forum — Recipes, Strategies & Discoveries",
  "/privacy-policy": "MystiCrafter Privacy Policy",
  "/delete-account": "Delete Your MystiCrafter Account",
};

const RouteMeta: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const base = ROUTE_TITLES[pathname];
    const title = base
      || (pathname.startsWith("/forums") ? "MystiCrafter Community Forum" : ROUTE_TITLES["/"]);
    document.title = title;
    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonical) canonical.href = `https://www.mysticrafter.com${pathname === "/" ? "/" : pathname}`;
  }, [pathname]);
  return null;
};

const AppContent: React.FC = () => {
  const [, setUser] = useState<any>(null);
  const [, setBestScore] = useState<number>(0);
  const { loadFromLocalStorage, saveToLocalStorage } = useElementStore();
  const [showCompletion, setShowCompletion] = useState(false);
  const totalElements = Object.keys(ELEMENT_DEFS).length;

  const syncGameStateToFirebase = useCallback(
    debounce(() => {
      const profile = loadUserProfile();
      if (profile.firebaseId) {
        syncGameState(profile.firebaseId).catch(console.error);
      }
    }, 5000),
    []
  );

  // Completion detection + Firestore sync on unlock changes
  useEffect(() => {
    const unsub = useElementStore.subscribe((state) => {
      if (state.unlocked.size >= totalElements) setShowCompletion(true);
      syncGameStateToFirebase();
    });
    return unsub;
  }, [syncGameStateToFirebase, totalElements]);

  // Sound system + background music on first interaction
  useEffect(() => {
    const cleanup = initSounds();
    const startMusic = () => {
      playSound("background");
      document.removeEventListener("click", startMusic);
    };
    document.addEventListener("click", startMusic);
    return () => {
      cleanup();
      document.removeEventListener("click", startMusic);
    };
  }, []);

  useEffect(() => {
    updateSoundPreferences();
  }, []);

  // Capacitor native plugins (no-op on web)
  useEffect(() => {
    initCapacitor();
  }, []);

  // Rewarded ads (H5 Games Ads on web) — no-op until enabled/approved.
  useEffect(() => {
    initRewardedAds();
  }, []);

  // Load saved game + auth
  useEffect(() => {
    loadFromLocalStorage();
    const unsub = initAuth(setUser);
    return () => unsub();
  }, [loadFromLocalStorage]);

  useEffect(() => {
    const profile = loadUserProfile();
    if (profile.firebaseId) {
      getUserBestScore(profile.firebaseId).then(setBestScore).catch(console.error);
    }
  }, []);

  // Periodic autosave
  useEffect(() => {
    const interval = setInterval(() => saveToLocalStorage(), 60000);
    return () => clearInterval(interval);
  }, [saveToLocalStorage]);

  return (
    <>
      <RouteMeta />
      <Routes>
        <Route path="/" element={<HomeScreen />} />
        <Route path="/play" element={<ForgeScreen />} />
        <Route path="/battle" element={<Screen title="Battle Arena" active={null} showBack contentClassName="mc-legacy"><BattleArena /></Screen>} />
        <Route path="/codex" element={<CodexScreen />} />
        <Route path="/ranks" element={<RanksScreen />} />
        <Route path="/you" element={<ProfileScreen />} />
        <Route path="/settings" element={<SettingsScreen />} />
        <Route path="/auth" element={<AuthScreen />} />
        <Route path="/forums" element={<Screen title="Community Forum" active={null} showBack contentClassName="mc-legacy"><ForumList /></Screen>} />
        <Route path="/forums/:forumId" element={<Screen title="Community Forum" active={null} showBack contentClassName="mc-legacy"><TopicList /></Screen>} />
        <Route path="/forums/:forumId/topics/:topicId" element={<Screen title="Thread" active={null} showBack contentClassName="mc-legacy"><MessageThread /></Screen>} />
        <Route path="/delete-account" element={<DeleteAccount />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {showCompletion && (
        <CompletionOverlay count={totalElements} onClose={() => setShowCompletion(false)} />
      )}
    </>
  );
};

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </BrowserRouter>
  );
}

function debounce(fn: Function, delay: number) {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: any[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}
